import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Label } from '@produzionejb/ui';
import type { MpgAssignment } from '@produzionejb/types';
import { ApiError } from '../../lib/api-client';
import {
  useCreateMpg,
  useMpg,
  useUpdateMpg,
  useCloseMpg,
  useDeleteMpg,
} from '../../lib/mpg-api';
import { LotPicker } from '../../components/LotPicker';
import { MpgMonthHistoryCard } from './MpgMonthHistoryCard';

type AssignmentRow = {
  key: string;
  operatorName: string;
  lotId: string;
  lotCode?: string;
  qty5g: string;
  qty10g: string;
  qty20g: string;
  qty50g: string;
  qty100g: string;
  weightG: string;
  notes?: string;
};

function newKey() {
  return Math.random().toString(36).slice(2, 9);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

function rowToAssignment(r: AssignmentRow): MpgAssignment {
  return {
    operatorName: r.operatorName.trim(),
    lotId: r.lotId,
    qty5g: Number(r.qty5g) || 0,
    qty10g: Number(r.qty10g) || 0,
    qty20g: Number(r.qty20g) || 0,
    qty50g: Number(r.qty50g) || 0,
    qty100g: Number(r.qty100g) || 0,
    weightG: Number(r.weightG) || 0,
    notes: r.notes?.trim() || null,
  };
}

export function MpgFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const mpgQ = useMpg(id);
  const createMut = useCreateMpg();
  const updateMut = useUpdateMpg(id ?? '');
  const closeMut = useCloseMpg(id ?? '');
  const deleteMut = useDeleteMpg();

  const [notes, setNotes] = useState('');
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [movFilter, setMovFilter] = useState<'ALL' | 'IN' | 'OUT'>('ALL');
  const [residuoOnlyPositive, setResiduoOnlyPositive] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const readOnly = isEdit && mpgQ.data?.status !== 'DRAFT';

  useEffect(() => {
    if (isEdit && mpgQ.data) {
      const m = mpgQ.data;
      setNotes(m.notes ?? '');
      setAssignments(
        m.assignments.map((a, idx) => ({
          key: `${idx}-${a.lotId}`,
          operatorName: a.operatorName,
          lotId: a.lotId,
          lotCode: a.lotCode ?? undefined,
          qty5g: String(a.qty5g ?? 0),
          qty10g: String(a.qty10g ?? 0),
          qty20g: String(a.qty20g ?? 0),
          qty50g: String(a.qty50g ?? 0),
          qty100g: String(a.qty100g ?? 0),
          weightG: String(a.weightG ?? 0),
          notes: a.notes ?? '',
        })),
      );
    }
  }, [isEdit, mpgQ.data]);

  function addAssignment() {
    setAssignments((rows) => [
      ...rows,
      {
        key: newKey(),
        operatorName: '',
        lotId: '',
        qty5g: '0',
        qty10g: '0',
        qty20g: '0',
        qty50g: '0',
        qty100g: '0',
        weightG: '0',
        notes: '',
      },
    ]);
  }
  function removeAssignment(key: string) {
    setAssignments((rows) => rows.filter((r) => r.key !== key));
  }
  function updateAssignment(key: string, patch: Partial<AssignmentRow>) {
    setAssignments((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  async function handleSave() {
    if (!id) return;
    setErrorMsg(null);
    try {
      const cleaned = assignments
        .filter((r) => r.operatorName.trim() && r.lotId)
        .map(rowToAssignment);
      await updateMut.mutateAsync({
        notes: notes.trim() || null,
        assignments: cleaned,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleClose() {
    if (!id || !mpgQ.data) return;
    if (!window.confirm(t('mpg.form.confirmClose', { date: formatDate(mpgQ.data.workDate) }))) return;
    setErrorMsg(null);
    try {
      // Salva eventuali modifiche pending
      const cleaned = assignments
        .filter((r) => r.operatorName.trim() && r.lotId)
        .map(rowToAssignment);
      await updateMut.mutateAsync({
        notes: notes.trim() || null,
        assignments: cleaned,
      });
      const closed = await closeMut.mutateAsync();
      if (closed.autoRolloverNextCode) {
        window.alert(t('mpg.form.rolloverCreated', { code: closed.autoRolloverNextCode }));
      }
      navigate('/documents/mpg');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleDelete() {
    if (!id || !mpgQ.data) return;
    const label = mpgQ.data.code ? `MPG ${mpgQ.data.code}` : 'MPG';
    if (!window.confirm(t('mpg.list.confirmDelete', { name: label }))) return;
    try {
      await deleteMut.mutateAsync(id);
      navigate('/documents/mpg');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  const movFiltered = useMemo(() => {
    if (!mpgQ.data) return [];
    if (movFilter === 'ALL') return mpgQ.data.movimenti;
    return mpgQ.data.movimenti.filter((m) => m.direction === movFilter);
  }, [mpgQ.data, movFilter]);

  const movTotalIn = useMemo(() => {
    if (!mpgQ.data) return 0;
    return mpgQ.data.movimenti
      .filter((m) => m.direction === 'IN')
      .reduce((s, m) => s + (Number(m.qty) || 0), 0);
  }, [mpgQ.data]);
  const movTotalOut = useMemo(() => {
    if (!mpgQ.data) return 0;
    return mpgQ.data.movimenti
      .filter((m) => m.direction === 'OUT')
      .reduce((s, m) => s + (Number(m.qty) || 0), 0);
  }, [mpgQ.data]);

  const residuoFiltered = useMemo(() => {
    if (!mpgQ.data) return [];
    if (residuoOnlyPositive) return mpgQ.data.residuo.filter((r) => Number(r.residualQty) > 0).slice(0, 50);
    return mpgQ.data.residuo.slice(0, 50);
  }, [mpgQ.data, residuoOnlyPositive]);

  const isSubmitting = createMut.isPending || updateMut.isPending || closeMut.isPending;

  if (!isEdit) {
    // Create-only page: button-driven creation already handled by list page. Redirect.
    return (
      <p className="text-sm text-slate-500">
        {t('mpg.form.createViaList')} <Button variant="secondary" onClick={() => navigate('/documents/mpg')}>{t('common.back')}</Button>
      </p>
    );
  }
  if (mpgQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (mpgQ.error || !mpgQ.data) {
    return (
      <p className="text-sm text-red-600">
        {t('mpg.form.errorLoad')}: {(mpgQ.error as Error | null)?.message ?? 'unknown'}
      </p>
    );
  }

  const m = mpgQ.data;
  const title = m.code ? `MPG ${m.code}` : t('mpg.form.titleEdit');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        <div className="flex items-center gap-2">
          <span
            className={
              m.status === 'DRAFT'
                ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
            }
          >
            {m.status === 'DRAFT' ? t('mpg.list.statusDraft') : t('mpg.list.statusClosed')}
          </span>
          <Button variant="secondary" onClick={() => navigate('/documents/mpg')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Card>
          <CardContent>
            <p className="text-sm text-red-600">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('mpg.sections.header')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>{t('mpg.fields.code')}</Label>
            <p className="font-mono text-sm">{m.code ? `MPG ${m.code}` : '—'}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t('mpg.fields.workDate')}</Label>
            <p className="text-sm">{formatDate(m.workDate)}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t('mpg.fields.createdAt')}</Label>
            <p className="text-sm text-slate-600">{new Date(m.createdAt).toLocaleString()}</p>
          </div>
          {m.closedAt && (
            <div className="space-y-1.5">
              <Label>{t('mpg.fields.closedAt')}</Label>
              <p className="text-sm text-slate-600">{new Date(m.closedAt).toLocaleString()}</p>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-3">
            <Label htmlFor="notes">{t('mpg.fields.notes')}</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('mpg.sections.assignments')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {assignments.length === 0 && (
            <p className="text-sm text-slate-500">{t('mpg.assignments.empty')}</p>
          )}
          {assignments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-2">{t('mpg.fields.operatorName')}</th>
                    <th className="px-2 py-2">{t('mpg.fields.lotto')}</th>
                    <th className="px-2 py-2 text-right">5g</th>
                    <th className="px-2 py-2 text-right">10g</th>
                    <th className="px-2 py-2 text-right">20g</th>
                    <th className="px-2 py-2 text-right">50g</th>
                    <th className="px-2 py-2 text-right">100g</th>
                    <th className="px-2 py-2 text-right">{t('mpg.fields.weightG')}</th>
                    <th className="px-2 py-2">{t('mpg.fields.note')}</th>
                    <th className="px-2 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100">
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className="w-28 rounded border border-slate-300 px-2 py-1 text-xs uppercase"
                          placeholder={t('mpg.assignments.operatorPlaceholder')}
                          value={row.operatorName}
                          onChange={(e) => updateAssignment(row.key, { operatorName: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <LotPicker
                          value={row.lotId}
                          valueCode={row.lotCode}
                          onChange={(id2, code) => updateAssignment(row.key, { lotId: id2, lotCode: code })}
                          disabled={readOnly}
                        />
                      </td>
                      {(['qty5g', 'qty10g', 'qty20g', 'qty50g', 'qty100g'] as const).map((field) => (
                        <td key={field} className="px-2 py-1 text-right">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="w-14 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                            value={row[field]}
                            onChange={(e) => updateAssignment(row.key, { [field]: e.target.value })}
                            disabled={readOnly}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-20 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                          value={row.weightG}
                          onChange={(e) => updateAssignment(row.key, { weightG: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          value={row.notes ?? ''}
                          onChange={(e) => updateAssignment(row.key, { notes: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeAssignment(row.key)}
                            className="text-red-600 hover:underline text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addAssignment}>
              + {t('mpg.assignments.addRow')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {t('mpg.sections.movements')} ({m.movimenti.length})
            </CardTitle>
            <div className="flex items-center gap-3">
              <select
                className="rounded border border-slate-300 px-2 py-1 text-xs"
                value={movFilter}
                onChange={(e) => setMovFilter(e.target.value as 'ALL' | 'IN' | 'OUT')}
              >
                <option value="ALL">{t('mpg.movements.filterAll')}</option>
                <option value="IN">{t('mpg.movements.filterIn')}</option>
                <option value="OUT">{t('mpg.movements.filterOut')}</option>
              </select>
              <span className="text-xs text-slate-600">
                {t('mpg.movements.totalIn')}: <span className="font-mono">{movTotalIn.toFixed(2)}g</span> /{' '}
                {t('mpg.movements.totalOut')}: <span className="font-mono">{movTotalOut.toFixed(2)}g</span>
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {movFiltered.length === 0 && <p className="p-4 text-sm text-slate-500">{t('mpg.movements.empty')}</p>}
          {movFiltered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">{t('mpg.fields.lotto')}</th>
                  <th className="px-3 py-2">{t('mpg.fields.prodotto')}</th>
                  <th className="px-3 py-2">{t('mpg.movements.direction')}</th>
                  <th className="px-3 py-2 text-right">{t('mpg.fields.qty')}</th>
                  <th className="px-3 py-2">{t('mpg.movements.sourceDoc')}</th>
                  <th className="px-3 py-2">{t('mpg.movements.ora')}</th>
                </tr>
              </thead>
              <tbody>
                {movFiltered.map((mv) => (
                  <tr key={mv.id} className="border-b border-slate-100">
                    <td className="px-3 py-1 font-mono text-xs">{mv.lotCode ?? '—'}</td>
                    <td className="px-3 py-1 text-xs">{mv.productCode ?? '—'}</td>
                    <td className="px-3 py-1">
                      <span
                        className={
                          mv.direction === 'IN'
                            ? 'rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700'
                            : mv.direction === 'OUT'
                              ? 'rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700'
                              : 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700'
                        }
                      >
                        {mv.direction}
                      </span>
                    </td>
                    <td className="px-3 py-1 text-right font-mono text-xs">{mv.qty}g</td>
                    <td className="px-3 py-1 font-mono text-xs">
                      {mv.sourceDocumentType ? `${mv.sourceDocumentType} ${mv.sourceDocumentCode ?? '—'}` : '—'}
                    </td>
                    <td className="px-3 py-1 text-xs text-slate-500">
                      {new Date(mv.occurredAt).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {t('mpg.sections.residuo')} ({m.residuo.length})
            </CardTitle>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={residuoOnlyPositive}
                onChange={(e) => setResiduoOnlyPositive(e.target.checked)}
              />
              {t('mpg.residuo.filterPositive')}
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {residuoFiltered.length === 0 && <p className="p-4 text-sm text-slate-500">{t('mpg.residuo.empty')}</p>}
          {residuoFiltered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">{t('mpg.fields.lotto')}</th>
                  <th className="px-3 py-2">{t('mpg.fields.prodotto')}</th>
                  <th className="px-3 py-2 text-right">{t('mpg.fields.residualQty')}</th>
                </tr>
              </thead>
              <tbody>
                {residuoFiltered.map((r) => (
                  <tr key={r.lotId} className="border-b border-slate-100">
                    <td className="px-3 py-1 font-mono text-xs">{r.lotCode}</td>
                    <td className="px-3 py-1 text-xs">{r.productCode} — {r.productName}</td>
                    <td className="px-3 py-1 text-right font-mono text-xs">{Number(r.residualQty).toFixed(2)}g</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {m.residuo.length > 50 && (
            <p className="p-2 text-xs text-slate-500">{t('mpg.residuo.top50')}</p>
          )}
        </CardContent>
      </Card>

      <MpgMonthHistoryCard periodId={m.periodId} />

      {!readOnly && (
        <div className="flex justify-between gap-2">
          <div>
            {assignments.length === 0 && (
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="!bg-red-50 !text-red-700 !border-red-200 hover:!bg-red-100"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSave} disabled={isSubmitting}>
              {t('mpg.form.saveButton')}
            </Button>
            <Button onClick={handleClose} disabled={isSubmitting}>
              {t('mpg.form.closeButton')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

